import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import moment from 'moment';
import ThemedSpinner from '../../components/themed/ThemedSpinner';
import ErrorMessage from '../../components/errormessage';
import { formatDate } from '../../utils/helpers';
import SettingsTable from '../../components/super/settings';
import AddSetting from '../../components/super/add-setting';
import AddManager from '../../components/super/add-manager';
import ContentGroupAccess from '../../components/super/content-group-access';
import PartnerTranslationsEditor from '../../components/super/PartnerTranslationsEditor';
import { enforceSlash } from '../../utils/helpers';
import { useStores } from '../../hooks/use-stores';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import SimpleTable from '../../components/super/SimpleTable';
import EditIcon from '@material-ui/icons/Edit';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import InputLabel from '@material-ui/core/InputLabel';
import {
    FormControlLabel,
    FormLabel, IconButton,
    Radio,
    RadioGroup,
    Switch,
} from '@material-ui/core';
import GreenCheckbox from '../../components/GreenCheckbox';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { json2csv } from 'json-2-csv';
import PersonIcon from '@material-ui/icons/Person';
import Clipboard from 'react-clipboard.js';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import TimerIcon from '@material-ui/icons/Timer';
import Countdown from 'react-countdown';
import CircularProgress from '@material-ui/core/CircularProgress';
import DeleteIcon from '@material-ui/icons/Delete';
import { FEATURE_SECTIONS, FEATURE_SETTING_NAMES, getFeatureSettingNames } from '../../constants/featureSettings';
import SendIcon from '@material-ui/icons/Send';

function PartnerView() {
    let { partnerId } = useParams();
    const { superStore, commonStore, authStore } = useStores();
    const TOKEN_TIMER = 1 * 60 * 1000;
    const [activeOnly, setActiveOnly] = React.useState(true);
    const [subscription, setSubscription] = React.useState('Total');
    const [metrics, setMetrics] = React.useState([]);

    const {
        loadingPartner,
        aclGroups,
        aclRulesForPrincipal,
        creatingAclLine,
        removingAclLines,
        loadingAclRulesForPrincipal,
        addingPartnerManager,
        removingPartnerManagers,
        removingPartnerSettings,
        uploadingPartnerLogo,
        uploadingPartnerFavicon,
        togglingPartner,
        addingPartnerSettings,
        editingPartner: partner,
        managerTokensIndex,
        error,
    } = superStore;
    const { pathname } = useLocation();
    const baseUrl = enforceSlash(pathname);
    const navigate = useNavigate();

    useEffect(() => {
        if (partner && partner.metrics) {
            setMetrics(partner.metrics);
        }
    }, [partner]);
    const billingManager =
        partner && partner.managers && partner.managers.find((x) => x.billing);

    const partnerSettings = partner?.settings || [];
    const systemSettings = superStore.systemSettings || [];
    const systemSettingsMap = superStore.systemSettingsMap;
    const [savingFeatureIds, setSavingFeatureIds] = useState(() => new Set());
    const [hideFeaturesInTable, setHideFeaturesInTable] = useState(false);

    const filterFeatureKeys = useMemo(
        () => new Set(getFeatureSettingNames()),
        []
    );

    const filteredPartnerSettings = partnerSettings.filter(
        (setting) =>
            setting &&
            typeof setting.name === 'string' &&
            !setting.name.startsWith('ui.translation.') &&
            (!hideFeaturesInTable || !filterFeatureKeys.has(setting.name))
    );

    const partnerSettingsMap = useMemo(
        () => new Map(partnerSettings.map((setting) => [setting.name, setting])),
        [partnerSettings]
    );

    const computeFeatureState = (feature) => {
        if (feature.relatedKeys.some((key) => partnerSettingsMap.has(key))) {
            return {
                checked: feature.resolve(partnerSettings),
                source: 'partner',
            };
        }

        if (feature.relatedKeys.some((key) => systemSettingsMap.has(key))) {
            return {
                checked: feature.resolve(systemSettings),
                source: 'system',
            };
        }

        return {
            checked: feature.resolve([]),
            source: null,
        };
    };

    const nestedPartnersFeature = useMemo(() => {
        const feature = FEATURE_SECTIONS.flatMap((s) => s.features).find(
            (f) => f.id === 'nested-partners'
        );
        if (!feature) return { checked: false, source: null };
        return computeFeatureState(feature);
    }, [partnerSettings, systemSettings, partnerSettingsMap, systemSettingsMap]);

    const isSubPartner = !!(partner && partner.parent_account_id);
    const nestedPartnersEnabled = !!nestedPartnersFeature.checked;
    const showDistributorActions =
        nestedPartnersEnabled && !isSubPartner && !!partner;

    const partnerIdForRequests = useMemo(() => {
        const numericId = Number(partnerId);
        return Number.isNaN(numericId) ? partnerId : numericId;
    }, [partnerId]);

    const loadPartner = async () => {
        await superStore.loadPartner(partnerId);
    };

    const loadData = async () => {
        await superStore.getSystemSettings();
        await loadPartner();
        await superStore.loadAclGroups();
        await superStore.loadLanguages();
    };

    const loadAclRulesForPartner = async () => {
        if (partner && partner.unique_id) {
            await superStore.loadAclRulesForPrincipal(partner.unique_id);
        }
    };

    const deleteAclRule = async (id) => {
        await superStore.removeAclLine(id);
        await loadAclRulesForPartner();
    };

    const createAclRule = async (principal_unique_id, unique_id, rule = 1) => {
        await superStore.createAclLine({
            rule,
            principal_unique_id,
            object_unique_id: unique_id,
        });
        await loadAclRulesForPartner();
    };

    const handleFeatureToggle = useCallback(
        async (feature, nextChecked) => {
            const mutations = feature.mutations[nextChecked ? 'true' : 'false'] || [];
            if (!mutations.length) return;

            setSavingFeatureIds((prev) => {
                const next = new Set(prev);
                next.add(feature.id);
                return next;
            });

            const revertedValues = [];

            try {
                for (const mutation of mutations) {
                    if (mutation.type === 'upsert') {
                        const existing = (partner?.settings || []).find(
                            (setting) => setting.name === mutation.key
                        );

                        if (existing) {
                            if (existing.value === mutation.value) continue;
                            revertedValues.push({ id: existing.id, value: existing.value });
                            superStore.updatePartnerSetting(existing.id, mutation.value);
                            await superStore.savePartnerSetting(existing.id, partnerIdForRequests);
                        } else {
                            await superStore.addPartnerSetting(partnerIdForRequests, {
                                name: mutation.key,
                                value: mutation.value,
                            });
                        }
                    } else if (mutation.type === 'delete') {
                        const existing = (partner?.settings || []).find(
                            (setting) => setting.name === mutation.key
                        );
                        if (existing) {
                            revertedValues.push({ id: existing.id, value: existing.value });
                            await superStore.removePartnerSetting(
                                existing.id,
                                partnerIdForRequests
                            );
                        }
                    }
                }
            } catch (error) {
                console.error(error);
                commonStore.error(`Failed to update ${feature.label}. Please try again.`);
                if (revertedValues.length) {
                    revertedValues.forEach(({ id, value }) => {
                        superStore.updatePartnerSetting(id, value);
                    });
                }
                await loadPartner();
            } finally {
                setSavingFeatureIds((prev) => {
                    const next = new Set(prev);
                    next.delete(feature.id);
                    return next;
                });
            }
        },
        [partner, superStore, partnerIdForRequests, partnerId, commonStore]
    );

    useEffect(() => {
        loadData();
    }, [partnerId]);

    useEffect(() => {
        loadAclRulesForPartner();
    }, [partner]);

    const addSetting = (name, value) => {
        const data = { name, value };
        superStore.addPartnerSetting(partnerId, data);
    };

    const onChangeSetting = (id, value) => {
        superStore.updatePartnerSetting(id, value);
        superStore.debouncedSavePartnerSetting(id, partnerId )
    };

    const onBlurSettingValue = (id, value) => {
        superStore.trimPartnerSetting(id, value);
    }

    const removeSetting = (settingId) => {
        superStore.removePartnerSetting(settingId, partnerId);
    };

    const handleCustomBillingToggle = async (event) => {
        const isEnabled = event.target.checked;
        const setting = (partner?.settings || []).find((s) => s.name === 'billing.custom');
        
        try {
            if (isEnabled) {
                // Enable custom billing
                // Backend will automatically set auto_collection to 'off' in Chargebee
                if (setting) {
                    // Update existing setting to '1'
                    superStore.updatePartnerSetting(setting.id, '1');
                    await superStore.savePartnerSetting(setting.id, Number(partnerId));
                } else {
                    // Create new setting
                    await superStore.addPartnerSetting(Number(partnerId), { name: 'billing.custom', value: '1' });
                }
            } else {
                // Disable custom billing
                if (setting) {
                    // Delete the setting (better than setting to '0')
                    await superStore.removePartnerSetting(setting.id, Number(partnerId));
                }
                // Note: We do NOT change auto_collection back when disabling custom billing
            }
            // Settings are already updated in the store, no need to reload entire partner
        } catch (error) {
            console.error('Error toggling custom billing:', error);
            commonStore.error('Failed to update custom billing setting');
        }
    };

    const addManager = (name, email, role, sendWelcome) => {
        void superStore.addPartnerManager(partnerId, { name, email, sendWelcome });
    };

    const onChangeBillingPerson = (user_id) => {
        void superStore.setBillingPerson(partnerId, { user_id });
    };

    const removeManager = (userId) => {
        superStore.removePartnerManager(userId, partnerId);
    };

    const deactivate = () => {
        commonStore.showConfirm(
            'By doing that, partner will be DISABLED and Chargebee subscriptions will be CANCELLED',
            'Deactivate',
            'Deactivate',
            async () => {
                await superStore.togglePartner(partnerId, false);
                await loadPartner();
            }
        );
    };

    const activate = () => {
        commonStore.showConfirm(
            'By doing that, partner will be ENABLED and Chargebee subscriptions will be RECREATED. Immediate invoice can be also issued',
            'Activate',
            'Activate',
            async () => {
                await superStore.togglePartner(partnerId, true);
                await loadPartner();
            }
        );
    };

    const [uploadError, setUploadError] = useState(null);
    const [uploading, setUploading] = useState(false);
    const handleLogoSelect = async (evt) => {
        let error = null;

        evt.stopPropagation();
        evt.preventDefault();

        const files = evt.target.files || evt.dataTransfer.files;
        const image = files[0];

        setUploading(true);

        // Error handling
        if (image.type !== 'image/png') {
            error = `'${image.type}' is not a supported format`;
        } else if (image.size > 80000) {
            error = `'${image.name}' is too large, please pick a smaller file`;
        }

        const formData = new FormData();
        formData.append('image', image);

        if (error) {
            setUploadError(error);
        } else {
            setUploadError(null);
            await superStore.uploadPartnerLogo(partnerId, formData);
        }
    };

    const handleFavIconSelect = async (evt) => {
        let error = null;

        evt.stopPropagation();
        evt.preventDefault();

        const files = evt.target.files || evt.dataTransfer.files;
        const image = files[0];

        setUploading(true);

        let accepted = [
            'image/png',
            'image/x-icon',
            'image/vnd.microsoft.icon',
        ];
        // Error handling
        if (!accepted.includes(image.type)) {
            error = `'${image.type}' is not a supported format`;
        } else if (image.size > 80000) {
            error = `'${image.name}' is too large, please pick a smaller file`;
        }

        const formData = new FormData();
        formData.append('image', image);

        if (error) {
            setUploadError(error);
        } else {
            setUploadError(null);
            await superStore.uploadPartnerFavicon(partnerId, formData);
        }
    };

    const logoFileRef = useRef();
    const faviconFileRef = useRef();

    const chooseFile = (fileRef) => {
        fileRef.current.click();
    };

    const handleDragOver = (evt) => {
        evt.stopPropagation();
        evt.preventDefault();
        evt.dataTransfer.dropEffect = 'copy'; // Explicitly show this is a copy.
    };

    const sendLoginInfo = async (manager) => {
        commonStore.showConfirm(
            `Do you want to send an email to ${manager.email} with a login link for the LMS?`,
            'Send email',
            'Send login email',
            async () => {
                const result = await authStore.signin(
                    manager.email,
                    null,
                    null,
                    null,
                    `/partner/${partnerId}/companies`,
                    'login-info'
                );
                if (result) commonStore.success('Login email sent!');
            }
        );
    };

    const columns = useMemo(
        () => [
            {
                Header: 'Created',
                accessor: 'created',
                Cell: (x) => formatDate(x.value),
            },
            {
                Header: 'Email',
                accessor: 'email',
            },
            {
                id: 'send',
                accessor: (x) => (
                    <Grid
                        container
                        direction="row"
                        alignItems="center"
                        spacing={1}
                    >
                        <Grid item>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                    sendLoginInfo(x);
                                }}
                                startIcon={<SendIcon />}
                            >
                                Send login info
                            </Button>
                        </Grid>
                    </Grid>
                ),
            },
            {
                Header: 'Name',
                accessor: 'name',
            },
            {
                id: 'impersonate',
                accessor: (x) => (
                    <Grid
                        container
                        direction="row"
                        alignItems="center"
                        spacing={1}
                    >
                        <Grid item>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                    impersonate(x);
                                }}
                                startIcon={<PersonIcon />}
                            >
                                Impersonate
                            </Button>
                        </Grid>
                        {managerTokensIndex[x.email] &&
                            managerTokensIndex[x.email].access_link && (
                                <Fragment>
                                    <Grid item>
                                        <Clipboard
                                            className="copy-to-clipboard-link"
                                            data-clipboard-text={
                                                managerTokensIndex[x.email]
                                                    .access_link
                                            }
                                        >
                                            <Button
                                                variant="contained"
                                                startIcon={<FileCopyIcon />}
                                            >
                                                Copy sign in link
                                            </Button>
                                        </Clipboard>
                                    </Grid>
                                    <Grid item>
                                        <TimerIcon color="primary" />
                                    </Grid>
                                    <Grid item>
                                        <Countdown
                                            date={Date.now() + TOKEN_TIMER}
                                            renderer={(props) => (
                                                <Fragment>
                                                    {props.minutes
                                                        .toString()
                                                        .padStart(2, '0') +
                                                        ':' +
                                                        props.seconds
                                                            .toString()
                                                            .padStart(2, '0')}
                                                </Fragment>
                                            )}
                                        />
                                    </Grid>
                                </Fragment>
                            )}
                    </Grid>
                ),
            },
            {
                id: 'removeManager',
                accessor: 'id',
                Cell: observer((x) => {
                    return (
                        removingPartnerManagers.includes(x.value) ? (
                            <CircularProgress size={25} />
                        ) : (
                            <IconButton
                                onClick={() => removeManager(x.value)}
                                color="secondary"
                                variant="contained"
                            >
                                <DeleteIcon fontSize="large" />
                            </IconButton>
                        )
                    );
                })
            }
        ],
        [managerTokensIndex]
    );

    const chartOptions = useMemo(() => {
        return {
            chart: {
                type: 'spline',
                zoomType: 'x',
                panning: true,
                panKey: 'shift',
                scrollablePlotArea: {
                    minWidth: 600,
                    minHeight: 400,
                },
                backgroundColor: {
                    linearGradient: [0, 0, 0, 110],
                    stops: [
                        [0, 'rgb(240, 246, 246, 0.0)'],
                        [1, 'rgb(240, 246, 246, 0.2)'],
                    ],
                },
            },
            colors: ['#82C9AB', '#F1D47A', '#FC814A', '#5B8FA8'],

            title: {
                text: 'Partner Stats',
            },

            plotOptions: {
                series: {
                    animation: {
                        duration: 1000,
                    },
                    marker: {
                        enabled: true,
                    },
                    lineWidth: 1.5,
                },
            },
            xAxis: {
                categories: metrics.map((x) =>
                    moment(x.timestamp).format('D MMM YY')
                ),
            },
            tooltip: {
                shared: true,
            },
            credits: {
                enabled: false,
            },
            yAxis: [
                {
                    title: {
                        text: 'User/Seat',
                    },
                },
                {
                    title: {
                        text: 'Companies count',
                    },
                    allowDecimals: false,
                    opposite: true,
                },
            ],

            series: [
                {
                    type: 'area',
                    yAxis: 1,
                    name: (activeOnly ? 'Active ' : '') + 'Companies Total',
                    marker: {
                        symbol: 'url(https://awarego.com/icon-solid-green.png)',
                        height: 5,
                        width: 5,
                    },
                    fillColor: {
                        linearGradient: {
                            y1: 0,
                            y2: 0.1,
                        },
                        stops: [
                            [0, 'rgba(130, 201, 171, 0.02)'],
                            [1, 'rgba(130, 201, 171, 0)'],
                        ],
                    },
                    data: metrics.map(
                        (x) =>
                            x[
                                activeOnly
                                    ? 'companies' + subscription + 'Active'
                                    : 'companies' + subscription
                            ]
                    ),
                },
                {
                    type: 'area',
                    yAxis: 0,
                    name: (activeOnly ? 'Active ' : '') + 'Seats',
                    fillColor: {
                        linearGradient: {
                            y1: 0,
                            y2: 0.1,
                        },
                        stops: [
                            [0, 'rgba(241, 212, 122, 0.02)'],
                            [1, 'rgba(241, 212, 122, 0)'],
                        ],
                    },

                    data: metrics.map(
                        (x) =>
                            x[
                                activeOnly
                                    ? 'seats' + subscription + 'Active'
                                    : 'seats' + subscription
                            ]
                    ),
                    marker: {
                        symbol: 'url(https://awarego.com/icon-solid-yellow.png)',
                        height: 5,
                        width: 5,
                    },
                },
                {
                    type: 'area',
                    yAxis: 0,
                    name: (activeOnly ? 'Active ' : '') + 'Users',
                    fillColor: {
                        linearGradient: {
                            y1: 0,
                            y2: 0.1,
                        },
                        stops: [
                            [0, 'rgba(252, 129, 74, 0.02)'],
                            [1, 'rgba(252, 129, 74, 0)'],
                        ],
                    },
                    data: metrics.map(
                        (x) =>
                            x[
                                activeOnly
                                    ? 'users' + subscription + 'Active'
                                    : 'users' + subscription
                            ]
                    ),
                    marker: {
                        symbol: 'url(https://awarego.com/icon-solid-orange.png)',
                        height: 5,
                        width: 5,
                    },
                },
                ...(isSubPartner
                    ? []
                    : [
                          {
                              type: 'area',
                              yAxis: 0,
                              name: 'MRR',
                              fillColor: {
                                  linearGradient: {
                                      y1: 0,
                                      y2: 0.1,
                                  },
                                  stops: [
                                      [0, 'rgba(91, 143, 168, 0.02)'],
                                      [1, 'rgba(91, 143, 168, 0)'],
                                  ],
                              },
                              data: metrics.map((x) => x.mrr),
                              marker: {
                                  symbol:
                                      'url(https://awarego.com/icon-solid-blue.png)',
                                  height: 5,
                                  width: 5,
                              },
                          },
                      ]),
            ],
        };
    }, [metrics, activeOnly, subscription, isSubPartner]);

    const convertToCSV = async (data) => {
        const csv = await json2csv(data);
        return csv;
    };

    const downloadCSV = async () => {
        const csvData = new Blob([await convertToCSV(metrics)], {
            type: 'text/csv',
        });
        const csvURL = URL.createObjectURL(csvData);
        const link = document.createElement('a');
        link.href = csvURL;
        link.download = `Partner-${partnerId}-stats.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleChangeActiveOnly = (event) => {
        setActiveOnly(event.target.checked);
    };

    const handleChangeSubscription = (event) => {
        setSubscription(event.target.value);
    };

    const logo = useMemo(() => {
        if (partner && partner.settings) {
            let s = partner.settings.find((x) => x.name === 'ui.logo');
            if (s) return s.value;
        }
    }, [partner]);

    const favicon = useMemo(() => {
        if (partner && partner.settings) {
            let s = partner.settings.find((x) => x.name === 'ui.favicon');
            if (s) return s.value;
        }
    }, [partner]);

    const impersonate = (manager) => {
        if (manager.timer) {
            clearTimeout(manager.timer);
            manager.timer = null;
        }
        let link = superStore.impersonatePartner(manager.email, partnerId);
        if (link) {
            manager.timer = setTimeout(() => {
                manager.timer = null;
                superStore.removeToken(manager.email);
            }, TOKEN_TIMER);
        }
    };

    return (loadingPartner && !partner) || togglingPartner ? (
        <ThemedSpinner />
    ) : (
        <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            align="center"
            spacing={2}
        >
            {partner && (
                <Fragment>
                    {(error || uploadError) && (
                        <Grid item xs={12}>
                            <ErrorMessage
                                error={error || uploadError}
                                errorType="error"
                            />
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <Box mt={4}>
                            <Typography variant="h2" gutterBottom>
                                Partner{partner && `: ${partner.name}`}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => navigate(`${baseUrl}edit`)}
                            startIcon={<EditIcon />}
                        >
                            Edit
                        </Button>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <Button
                            variant="contained"
                            color="default"
                            onClick={() => navigate(`/partners/${partner.id}/companies/`)}
                        >
                            View Companies
                        </Button>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <Button
                            variant="contained"
                            color={partner.enabled ? 'secondary' : 'primary'}
                            onClick={partner.enabled ? deactivate : activate}
                        >
                            {partner.enabled ? 'Deactivate' : 'Activate'}
                        </Button>
                    </Grid>
                    {showDistributorActions && (
                        <Grid item xs={6} md={2}>
                            <Button
                                variant="contained"
                                color="default"
                                onClick={() =>
                                    navigate(
                                        `/partners/${partner.id}/subpartners/create`
                                    )
                                }
                            >
                                Add a distributor
                            </Button>
                        </Grid>
                    )}
                    {isSubPartner && (
                        <Grid item xs={12}>
                            <Typography variant="body2" color="textSecondary">
                                Distributor under partner #{partner.parent_account_id}
                            </Typography>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <Typography
                            variant="body1"
                            gutterBottom
                            color={partner.enabled ? 'primary' : 'secondary'}
                        >
                            <strong>Status: </strong>{' '}
                            {partner.enabled ? 'Active' : 'Disabled'}
                        </Typography>
                    </Grid>

                    {metrics && metrics.length > 0 && (
                        <>
                            <Box display={'flex'}>
                                <Box display={'flex'} flexDirection={'column'}>
                                    <Button
                                        onClick={downloadCSV}
                                        variant={'contained'}
                                        color={'primary'}
                                    >
                                        Export Raw data
                                    </Button>
                                    <FormControlLabel
                                        control={
                                            <GreenCheckbox
                                                checked={activeOnly}
                                                onChange={
                                                    handleChangeActiveOnly
                                                }
                                                name="checkedG"
                                            />
                                        }
                                        label="Active only"
                                    />
                                    <FormControl component="fieldset">
                                        <FormLabel component="legend">
                                            Subscription
                                        </FormLabel>
                                        <RadioGroup
                                            aria-label="gender"
                                            name="gender1"
                                            value={subscription}
                                            onChange={handleChangeSubscription}
                                        >
                                            <FormControlLabel
                                                value="Total"
                                                control={<Radio />}
                                                label="Total"
                                            />
                                            <FormControlLabel
                                                value="Free"
                                                control={<Radio />}
                                                label="Free"
                                            />
                                            <FormControlLabel
                                                value="Complete"
                                                control={<Radio />}
                                                label="Complete"
                                            />
                                            <FormControlLabel
                                                value="Training"
                                                control={<Radio />}
                                                label="Training"
                                            />
                                            <FormControlLabel
                                                value="Assessment"
                                                control={<Radio />}
                                                label="Assessment"
                                            />
                                        </RadioGroup>
                                    </FormControl>
                                </Box>

                                <Box minHeight={400}>
                                    <HighchartsReact
                                        highcharts={Highcharts}
                                        options={chartOptions}
                                    />
                                </Box>
                            </Box>
                            {showDistributorActions &&
                                partner.subPartners &&
                                partner.subPartners.length > 0 && (
                                    <Box mt={3} mb={2}>
                                        <Typography variant="h5" gutterBottom>
                                            Distributors
                                        </Typography>
                                        {partner.subPartners.map((sp) => (
                                            <Box
                                                key={sp.id}
                                                mb={1}
                                                display="flex"
                                                justifyContent="space-between"
                                                alignItems="center"
                                            >
                                                <Link
                                                    to={`/partners/${sp.id}`}
                                                    style={{
                                                        textDecoration: 'none',
                                                    }}
                                                >
                                                    <Typography color="primary">
                                                        {sp.name}
                                                    </Typography>
                                                </Link>
                                                <Button
                                                    size="small"
                                                    onClick={() =>
                                                        navigate(
                                                            `/partners/${sp.id}/companies`
                                                        )
                                                    }
                                                >
                                                    Companies
                                                </Button>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                        </>
                    )}

                    {partner.enabled && !isSubPartner ? (
                        <Fragment>
                            {partner.subscription && (
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body1" gutterBottom>
                                        <strong>
                                            Chargebee Subscription:{' '}
                                        </strong>{' '}
                                        {partner.subscription.id}
                                    </Typography>
                                </Grid>
                            )}
                            {partner.subscription &&
                                !partner.subscription.custom && (
                                    <Grid item xs={12} md={6}>
                                        <Typography
                                            variant="body1"
                                            gutterBottom
                                        >
                                            <strong>
                                                Chargebee Customer:{' '}
                                            </strong>{' '}
                                            {partner.subscription.customer_id}
                                        </Typography>
                                    </Grid>
                                )}
                            {partner.subscription &&
                                !partner.subscription.custom && (
                                    <Grid item xs={12} md={6}>
                                        <Typography
                                            variant="body1"
                                            gutterBottom
                                        >
                                            {partner.subscription.haveValidCard
                                                ? 'Billing info is OK'
                                                : 'No Billing info'}
                                        </Typography>
                                    </Grid>
                                )}
                            {partner.subscription && partner.enabled && (
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body1" gutterBottom>
                                        <strong>Next Payment: </strong>{' '}
                                        {moment
                                            .unix(
                                                partner.subscription
                                                    .next_billing_at
                                            )
                                            .format('LLL')}
                                        ,{' '}
                                        {(
                                            Math.max(
                                                partner.subscription
                                                    .plan_amount +
                                                    partner.subscription
                                                        .unbilled_charges -
                                                    partner.subscription
                                                        .promotional_credits -
                                                    partner.subscription
                                                        .refundable_credits,
                                                0
                                            ) / 100
                                        ).toFixed(2) + ' USD'}
                                    </Typography>
                                </Grid>
                            )}
                            {partner.subscription &&
                                !partner.subscription.custom && (
                                    <Fragment>
                                        <Grid item xs={12} md={6}>
                                            <Typography
                                                variant="body1"
                                                gutterBottom
                                            >
                                                <strong>
                                                    Plan amount (users):{' '}
                                                </strong>
                                                {
                                                    partner.subscription
                                                        .plan_quantity
                                                }
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Typography
                                                variant="body1"
                                                gutterBottom
                                            >
                                                <strong>
                                                    Plan amount (USD):{' '}
                                                </strong>
                                                {(
                                                    partner.subscription
                                                        .plan_amount / 100
                                                ).toFixed(2)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Typography
                                                variant="body1"
                                                gutterBottom
                                            >
                                                <strong>
                                                    Unbilled Charges:{' '}
                                                </strong>
                                                {(
                                                    partner.subscription
                                                        .unbilled_charges / 100
                                                ).toFixed(2)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Typography
                                                variant="body1"
                                                gutterBottom
                                            >
                                                <strong>
                                                    Promotional Credits:{' '}
                                                </strong>
                                                {(
                                                    partner.subscription
                                                        .promotional_credits /
                                                    100
                                                ).toFixed(2)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Typography
                                                variant="body1"
                                                gutterBottom
                                            >
                                                <strong>
                                                    Refundable Credits:{' '}
                                                </strong>
                                                {(
                                                    partner.subscription
                                                        .refundable_credits /
                                                    100
                                                ).toFixed(2)}
                                            </Typography>
                                        </Grid>
                                    </Fragment>
                                )}
                        </Fragment>
                    ) : (
                        ''
                    )}
                    <Grid item xs={12} container>
                        <Grid item xs={12} sm={6}>
                            {logo && (
                                <img src={logo} style={{ maxWidth: '200px' }} />
                            )}
                            <div className="company-logo-drop-zone">
                                <div
                                    className="d-flex flex-center flex-fill flex-column"
                                    style={{ height: '220px' }}
                                    onDragOver={handleDragOver}
                                    onDrop={handleLogoSelect}
                                >
                                    <img
                                        src="/static/img/drop-icon.svg"
                                        alt=""
                                    />
                                    {uploadingPartnerLogo || uploading ? (
                                        <div> Uploading...</div>
                                    ) : (
                                        <div style={{ textAlign: 'center' }}>
                                            <div id="drop_zone">
                                                Drag and drop
                                            </div>
                                            <div className="drop_zone_description">
                                                {' '}
                                                or{' '}
                                                <Button
                                                    color={'secondary'}
                                                    onClick={(e) => {
                                                        chooseFile(
                                                            logoFileRef,
                                                            e
                                                        );
                                                    }}
                                                >
                                                    browse
                                                </Button>{' '}
                                                for an image file
                                            </div>
                                        </div>
                                    )}
                                    <input
                                        className="hidden"
                                        type="file"
                                        multiple={false}
                                        accept="image/png"
                                        onChange={handleLogoSelect}
                                        ref={logoFileRef}
                                    />
                                </div>
                            </div>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            {favicon && (
                                <img
                                    src={favicon}
                                    style={{ maxWidth: '200px' }}
                                />
                            )}
                            <div className="company-logo-drop-zone">
                                <div
                                    className="d-flex flex-center flex-fill flex-column"
                                    style={{ height: '220px' }}
                                    onDragOver={handleDragOver}
                                    onDrop={handleFavIconSelect}
                                >
                                    <img
                                        src="/static/img/drop-icon.svg"
                                        alt=""
                                    />
                                    {uploadingPartnerFavicon ? (
                                        <div> Uploading...</div>
                                    ) : (
                                        <div style={{ textAlign: 'center' }}>
                                            <div id="drop_zone">
                                                Drag and drop
                                            </div>
                                            <div className="drop_zone_description">
                                                {' '}
                                                or{' '}
                                                <Button
                                                    color={'secondary'}
                                                    onClick={(e) => {
                                                        chooseFile(
                                                            faviconFileRef,
                                                            e
                                                        );
                                                    }}
                                                >
                                                    browse
                                                </Button>{' '}
                                                for an image file
                                            </div>
                                        </div>
                                    )}
                                    <input
                                        className="hidden"
                                        type="file"
                                        multiple={false}
                                        accept="image/png,image/x-icon,image/vnd.microsoft.icon"
                                        onChange={handleFavIconSelect}
                                        ref={faviconFileRef}
                                    />
                                </div>
                            </div>
                            <div className="company-logo-drop-zone-text">
                                The image file should be in PNG, up to 640 x 264
                                pixels and no bigger than 80KB
                            </div>
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <Box mt={2} display="flex" flexDirection="column" alignItems="center">
                            <Typography variant="h4" gutterBottom>
                                Settings
                            </Typography>
                            {!isSubPartner && (
                                <>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={(partner?.settings || []).some(s => s.name === 'billing.custom' && s.value === '1')}
                                        onChange={handleCustomBillingToggle}
                                        color="primary"
                                    />
                                }
                                label="Custom Billing"
                                labelPlacement="start"
                            />
                            <Typography variant='caption'>
                                Note: switching Custom Billing on will overwrite customer chargebee email to: sales@awarego.com and set autoCollection: off, switching it off will NOT return previous values
                            </Typography>
                                </>
                            )}
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Box mt={4} width="100%">
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                Experimental feature, use with care.
                            </Alert>
                            <Typography variant="h4" gutterBottom>
                                Features
                            </Typography>
                        </Box>
                    </Grid>
                    {FEATURE_SECTIONS.map((section) => (
                        <Grid item xs={12} key={section.id}>
                            <Accordion defaultExpanded={false} sx={{ width: '100%' }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="h5">{section.label}</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container spacing={2}>
                                        {section.features.map((feature, index) => {
                                            const { checked, source } = computeFeatureState(feature);
                                            const isSaving = savingFeatureIds.has(feature.id);

                                            return (
                                                <Grid
                                                    item
                                                    xs={12}
                                                    md={6}
                                                    key={feature.id}
                                                    sx={{ pl: { md: index % 2 === 0 ? 2 : 0 } }}
                                                >
                                                    <Box display="flex" flexDirection="column" alignItems="flex-start">
                                                        <Box display="flex" alignItems="center" gap={1.5}>
                                                            <Switch
                                                                color="primary"
                                                                checked={Boolean(checked)}
                                                                onChange={(event) =>
                                                                    handleFeatureToggle(
                                                                        feature,
                                                                        event.target.checked
                                                                    )
                                                                }
                                                                disabled={isSaving}
                                                            />
                                                            <Typography variant="subtitle1">
                                                                {feature.label} ({feature.relatedKeys.join(', ')})
                                                            </Typography>
                                                            {source === 'system' && (
                                                                <Chip
                                                                    label="System"
                                                                    size="small"
                                                                    color="warning"
                                                                    variant="outlined"
                                                                />
                                                            )}
                                                            {isSaving && <CircularProgress size={16} />}
                                                        </Box>
                                                        <Typography
                                                            variant="body2"
                                                            color="textSecondary"
                                                            sx={{ mt: 1 }}
                                                        >
                                                            {feature.description}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>
                        </Grid>
                    ))}
                    <Grid item xs={12}>
                        <AddSetting
                            saveSetting={addSetting}
                            addingSetting={addingPartnerSettings}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    color="primary"
                                    checked={hideFeaturesInTable}
                                    onChange={(event) =>
                                        setHideFeaturesInTable(event.target.checked)
                                    }
                                />
                            }
                            label="Hide Features"
                        />
                    </Grid>
                    {filteredPartnerSettings.length > 0 && (
                        <Grid item xs={10} md={6} style={{ maxWidth: '100%', paddingTop: '25px' }}>
                            <SettingsTable
                                settings={filteredPartnerSettings}
                                removeSetting={removeSetting}
                                removingSettings={removingPartnerSettings}
                                onChangeSettingValue={onChangeSetting}
                                onBlurSettingValue={onBlurSettingValue}
                            />
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <Accordion defaultExpanded={false}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="translation-overrides-content"
                                id="translation-overrides-header"
                            >
                                <Typography variant="h4">
                                    Translation Overrides
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails style={{ display: 'block', padding: 0 }}>
                                <PartnerTranslationsEditor 
                                    partnerId={partnerId}
                                    partnerSettings={partner.settings || []}
                                />
                            </AccordionDetails>
                        </Accordion>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="h4" gutterBottom>
                            Super Admins
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <AddManager
                            addingManager={addingPartnerManager}
                            addManager={addManager}
                            hideRoleSelector
                        />
                    </Grid>
                    {partner.managers && partner.managers.length > 0 && (
                        <Fragment>
                            <Grid item xs={12}>
                                <SimpleTable
                                    columns={columns}
                                    data={partner.managers}
                                    needsFilter={partner.managers.length > 10}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <Box mb={4}>
                                    <FormControl variant="filled" fullWidth>
                                        <InputLabel>Billing Contact</InputLabel>
                                        <Select
                                            variant="filled"
                                            name="billingManager"
                                            value={
                                                billingManager &&
                                                billingManager.user_id
                                            }
                                            onChange={(e) =>
                                                onChangeBillingPerson(
                                                    Number(e.target.value)
                                                )
                                            }
                                        >
                                            {partner &&
                                                partner.managers &&
                                                partner.managers.map(
                                                    (manager) => (
                                                        <MenuItem
                                                            key={
                                                                manager.user_id
                                                            }
                                                            value={
                                                                manager.user_id
                                                            }
                                                        >
                                                            {manager.name} &lt;
                                                            {manager.email}&gt;
                                                        </MenuItem>
                                                    )
                                                )}
                                        </Select>
                                    </FormControl>
                                </Box>
                            </Grid>
                        </Fragment>
                    )}
                    {partner && aclGroups.length > 0 && (
                        <>
                            <Grid item xs={12}>
                                <Typography variant="h4">
                                    Content Group Access
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <ContentGroupAccess
                                    aclGroups={aclGroups}
                                    aclRules={aclRulesForPrincipal}
                                    principalUniqueId={partner.unique_id}
                                    addToGroup={createAclRule}
                                    removeFromGroup={deleteAclRule}
                                    showLoader={
                                        creatingAclLine ||
                                        removingAclLines.length > 0 ||
                                        loadingAclRulesForPrincipal
                                    }
                                />
                            </Grid>
                        </>
                    )}
                </Fragment>
            )}
        </Grid>
    );
}

export default observer(PartnerView);
